import React, { FC } from 'react';
import { Theme, useTheme } from '@mui/material';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';

import { IScheduledAbsenceDTO } from 'src/api/dtos';
import { ArrowRightIcon, EmptyState } from 'src/shared/components';
import { DATE_TIME_FORMAT, DAY_COMMA_DATE_FORMAT } from 'src/shared/constants';
import { useTranslate } from 'src/shared/providers';
import { formatTime, isSameDay } from 'src/shared/utils';
import { getTimeFormat } from 'src/store/user/user.selectors';
import './ScheduledAbsences.styles.scss';

interface Props {
  scheduledAbsences: IScheduledAbsenceDTO[];
}

export const ScheduledAbsences: FC<Props> = ({ scheduledAbsences }) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const timeFormat = useSelector(getTimeFormat);

  if (!scheduledAbsences.length) {
    return (
      <EmptyState
        className="mt-4"
        title={t('absences.nothingToShow')}
        message={t('absences.youHaveNoUpcomingPlannedAbsences')}
        size="small"
      />
    );
  }

  return (
    <div className="pt-3 pe-1 scheduled-absences-wrapper">
      {scheduledAbsences.map((absence, index) => {
        const startDate = moment(absence.startDate).format(DAY_COMMA_DATE_FORMAT);
        const endDate = moment(absence.endDate).format(DAY_COMMA_DATE_FORMAT);
        const startTime = absence.startTime ? formatTime(absence.startTime, timeFormat, DATE_TIME_FORMAT) : undefined;
        const endTime = absence.endTime ? formatTime(absence.endTime, timeFormat, DATE_TIME_FORMAT) : undefined;

        return (
          <div key={`scheduled-absence-${index}`} className="scheduled-absence-card mb-2 border small">
            <ScheduleAbsence
              startDate={startDate}
              endDate={endDate}
              startTime={startTime}
              endTime={endTime}
              theme={theme}
            />
            <span className="text-muted fw-light">{absence.timeCategoryName}</span>
          </div>
        );
      })}
    </div>
  );
};

const ScheduleAbsence: FC<{
  startDate: string;
  endDate: string;
  startTime: string | undefined;
  endTime: string | undefined;
  theme: Theme;
}> = ({ startDate, endDate, startTime, endTime, theme }) => {
  if (startTime) {
    return (
      <div>
        <span>{startDate}</span>{' '}
        <span style={{ color: theme.palette.primary.main }}>
          ({startTime} - {endTime})
        </span>
      </div>
    );
  }

  if (isSameDay(startDate, endDate)) {
    return <div>{startDate}</div>;
  }

  return (
    <div className="d-flex align-items-center">
      <span>{startDate}</span>
      <ArrowRightIcon className="mx-2" size={16} color={theme.palette.primary.main} />
      <span>{endDate}</span>
    </div>
  );
};
